import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { ContaComSaldo, Transacao, TipoTransacao, NovaTransacaoManual, FaturamentoPeriodo } from '@pgcustom/shared-types';

/** Filtros aceitos por `GET /financeiro/transacoes` (`listarTransacoesQuerySchema`, `apps/api/src/modules/financeiro/financeiro.schema.ts`). */
export interface FiltroListaTransacoes {
  contaId?: string;
  tipo?: TipoTransacao;
  origem?: string;
  limit?: number;
  offset?: number;
}

/**
 * Adapter do módulo Financeiro no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/financeiro/*`
 * (`apps/api/src/modules/financeiro/financeiro.routes.ts`). Mesmo padrão
 * de `PedidosApi`/`ClientesApi`: `listarContas`/`listarTransacoes`
 * devolvem `Resultado<T>` (alimentam estados de UI diretamente);
 * `criarConta`/`criarTransacao` propagam `ApiError` — quem decide como
 * exibir a falha é a tela que chama.
 */
export class FinanceiroApi {
  constructor(private readonly api: ApiClient) {}

  async listarContas(): Promise<Resultado<ContaComSaldo[]>> {
    try {
      const resposta = await this.api.get<{ contas: ContaComSaldo[] }>('/financeiro/contas');
      if (resposta.contas.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.contas };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar contas',
      };
    }
  }

  async listarTransacoes(filtro: FiltroListaTransacoes): Promise<Resultado<Transacao[]>> {
    try {
      const resposta = await this.api.get<{ transacoes: Transacao[] }>('/financeiro/transacoes', { ...filtro });
      if (resposta.transacoes.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.transacoes };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar transações',
      };
    }
  }

  /** Detalhamento de Faturamento por mês — `GET /financeiro/faturamento-por-periodo`, agregação real no Postgres. */
  async obterFaturamentoPorPeriodo(meses = 6): Promise<Resultado<FaturamentoPeriodo[]>> {
    try {
      const resposta = await this.api.get<{ periodos: FaturamentoPeriodo[] }>('/financeiro/faturamento-por-periodo', {
        meses,
      });
      if (resposta.periodos.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.periodos };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar faturamento por período',
      };
    }
  }

  async criarConta(nome: string): Promise<ContaComSaldo> {
    const resposta = await this.api.post<{ conta: ContaComSaldo }>('/financeiro/contas', { nome });
    return resposta.conta;
  }

  async criarTransacao(dado: NovaTransacaoManual): Promise<Transacao> {
    const resposta = await this.api.post<{ transacao: Transacao }>('/financeiro/transacoes', dado);
    return resposta.transacao;
  }
}
